/**
 * Composable for managing recent history tracking.
 * Uses Inertia.js for reactive data and server communication.
 *
 * Display strings (label, subtitle, badge) are generated server-side
 * from model configurations, not stored in the database.
 */

import { router, usePage } from "@inertiajs/vue3";
import { computed, type ComputedRef } from "vue";

import type { RecentHistoryItem, SharedCrudData } from "../types";

/**
 * Window event opening the global AllRecentsSheet (mounted by the
 * always-present SidebarRecentItems widget). Dispatch from anywhere via
 * `openAllRecents()` to surface the full recent-history list.
 */
export const OPEN_ALL_RECENTS_EVENT = "crud:open-all-recents";

/** Open the global AllRecentsSheet from anywhere in the portal. */
export function openAllRecents(): void {
    window.dispatchEvent(new CustomEvent(OPEN_ALL_RECENTS_EVENT));
}

interface UseRecentHistoryReturn {
    /** All recent history grouped by model */
    recentHistory: ComputedRef<Record<string, RecentHistoryItem[]>>;
    /** All recent items flattened across models, sorted by opened_at desc */
    allRecentItems: ComputedRef<RecentHistoryItem[]>;
    /** Get recent items for a specific model */
    getRecentForModel: (
        model: string,
        limit?: number
    ) => ComputedRef<RecentHistoryItem[]>;
    /** Track an item as recently viewed (display strings generated server-side) */
    trackItem: (model: string, recordId: string | number) => void;
    /** Remove an item from history */
    removeItem: (model: string, recordId: string | number) => void;
    /** Clear history for a model or all history */
    clearHistory: (model?: string) => void;
    /** Check if an item was opened on the current device */
    isCurrentDevice: (deviceId: string) => boolean;
    /** Current device ID */
    deviceId: ComputedRef<string | undefined>;
}

/**
 * Composable for managing recent history.
 *
 * @example
 * ```ts
 * const { recentHistory, getRecentForModel, trackItem } = useRecentHistory();
 *
 * // Get recent items for a model (reactive)
 * const recentTransactions = getRecentForModel('transaction', 5);
 *
 * // Track an item (display strings generated server-side from model config)
 * trackItem('transaction', record.id);
 * ```
 */
export function useRecentHistory(): UseRecentHistoryReturn {
    const page = usePage<{ crud: SharedCrudData }>();

    const recentHistory = computed(() => page.props.crud?.recent_history ?? {});

    const allRecentItems = computed(() =>
        Object.values(recentHistory.value)
            .flat()
            .sort(
                (a, b) =>
                    new Date(b.opened_at).getTime() -
                    new Date(a.opened_at).getTime()
            )
    );

    const deviceId = computed(() => page.props.crud?.device_id);

    function getRecentForModel(
        model: string,
        limit = 5
    ): ComputedRef<RecentHistoryItem[]> {
        return computed(() => (recentHistory.value[model] ?? []).slice(0, limit));
    }

    /**
     * Track an item as recently viewed.
     *
     * Display strings (label, subtitle, badge) are generated server-side
     * from the model's recentHistory configuration.
     */
    function trackItem(model: string, recordId: string | number): void {
        router.post(
            "/crud/history/track",
            {
                model,
                record_id: String(recordId)
            },
            {
                preserveState: true,
                preserveScroll: true
            }
        );
    }

    function removeItem(model: string, recordId: string | number): void {
        router.delete(`/crud/history/${model}/${String(recordId)}`, {
            preserveState: true,
            preserveScroll: true
        });
    }

    function clearHistory(model?: string): void {
        const url = model ? `/crud/history/${model}` : "/crud/history";
        router.delete(url, {
            preserveState: true,
            preserveScroll: true
        });
    }

    function isCurrentDevice(itemDeviceId: string): boolean {
        return deviceId.value === itemDeviceId;
    }

    return {
        recentHistory,
        allRecentItems,
        getRecentForModel,
        trackItem,
        removeItem,
        clearHistory,
        isCurrentDevice,
        deviceId
    };
}
